import React from "react"
import { ReactSVG } from "react-svg"

import { graphql, Link } from "gatsby"
import { DatoCmsSlugsConfiguration, FooterFieldsFragment } from "../../../../graphql-types"

import * as styles from "./footer.module.scss"
import scrollUpArrow from "../../../../static/vectors/scrollUpArrow.svg"
import { useLocale } from "../../../context/locale-context"
import useSlugValue from "../../../hooks/use-slug-value"

type FooterProps = {
  data: FooterFieldsFragment
  slugs: DatoCmsSlugsConfiguration
  borderless?: boolean
}

const Footer: React.FC<FooterProps> = ({ data, slugs, borderless }) => {
  const { locale, defaultLocale } = useLocale()

  const cookiesPageSlug = useSlugValue(slugs._allCookiesPageSlugLocales, locale)
  const confidentialityPolicyPageSlug = useSlugValue(slugs._allConfidentialityPolicyPageSlugLocales, locale)
  const creditsPageSlug = useSlugValue(slugs._allCreditsPageSlugLocales, locale)
  const legalMentionsPageSlug = useSlugValue(slugs._allLegalMentionsPageSlugLocales, locale)

  const scrollToUp = () => {
    let rootElement = document.documentElement
    rootElement.scrollTo({
      top: 0,
      behavior: "smooth",
    })
  }

  return (
    <footer className={styles.footer}>
      <div className={styles.footerContainer}>
          <img src={data.v2Logo.url} alt={data.v2Logo.alt || ""} className={styles.logo}/>

            <div className={styles.siteMapContainer}>
              <p className={styles.sectionTitle}>{data.v2SiteMapTitle}</p>
              <div className={styles.siteMapLinks}>
                {data.v2SiteMapLinks.map((link, index: number) => (
                  <div key={index}>
                    {link?.isAnchorInPage ? (
                      <span
                        onClick={() => {
                          const anchor = document.getElementById(link?.anchorSection)
                          if (anchor) {
                            anchor.scrollIntoView({ behavior: "smooth" })
                          }
                        }}
                        className={styles.siteMapLink}
                      >
                        {link?.title}
                      </span>
                    ) : (
                      <Link
                        to={
                        link?.isInternalPage
                          ? locale === defaultLocale
                            ? `/${link?.internalLink?.slug}`
                            : `/${locale}/${link?.internalLink?.slug}`
                          : link?.externalLink
                      }
                        target={!link.isInternalPage && "_blank"}
                        className={styles.siteMapLink}
                      >
                        {link.title}
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className={styles.stayConnectedContainer}>
              <div>
                <p className={styles.stayConnectedTitle}>{data.stayConnectedTitle}</p>
                <div className={styles.stayConnectedButtonContainer}>
                  {data.socialMedias.map((socialMedia, index: number) => (
                    <div key={index} className={styles.stayConnectedButton}>
                      <a
                        href={socialMedia?.link}
                        target="_blank"
                        aria-label={socialMedia?.image?.alt}
                      >
                        {socialMedia?.image?.format === "svg" ? (
                          <ReactSVG src={socialMedia?.image?.url} />
                        ) : (
                          <img src={socialMedia?.image?.url} alt={socialMedia?.image?.alt || ""} />
                        )}
                      </a>
                    </div>
                  ))}
                </div>
              </div>
              {data.groupLink?.isAnchorInPage ? (
                <span
                  onClick={() => {
                    const anchor = document.getElementById(data.groupLink?.anchorSection)
                    if (anchor) {
                      anchor.scrollIntoView({ behavior: "smooth" })
                    }
                  }}
                  className={styles.siteMapLink}
                >
                  {data.groupLink?.title}
                </span>
              ) : (
                <Link
                  to={
                    data.groupLink?.isInternalPage
                      ? locale === defaultLocale
                        ? `/${data.groupLink?.internalLink?.slug}`
                        : `/${locale}/${data.groupLink?.internalLink?.slug}`
                      : data.groupLink?.externalLink
                  }
                  target={!data.groupLink?.isInternalPage && "_blank"}
                  className={styles.siteMapLink}>
                  {data.groupLink?.title}
                </Link>
              )}
            </div>
          <button onClick={() => scrollToUp()} className={styles.arrowUp} title={data.v2BackToTopLabel}>
            <ReactSVG src={scrollUpArrow} />
          </button>
      </div>
      <nav className={styles.sectionLegal} role="navigation">
          <ul>
            <li>
              <Link to={defaultLocale === locale ? `/${legalMentionsPageSlug}` : `/${locale}/${legalMentionsPageSlug}`}>
                <span>{data.legalMentions}</span>
              </Link>
            </li>
            &nbsp;&nbsp;&nbsp;&nbsp;
            <li>
              <Link
                to={
                  defaultLocale === locale
                    ? `/${confidentialityPolicyPageSlug}`
                    : `/${locale}/${confidentialityPolicyPageSlug}`
                }
              >
                <span>{data.confidentialityPolicy}</span>
              </Link>
            </li>
            &nbsp;&nbsp;&nbsp;&nbsp;
            <li>
              <Link to={defaultLocale === locale ? `/${cookiesPageSlug}` : `/${locale}/${cookiesPageSlug}`}>
                <span>{data.cookies}</span>
              </Link>
            </li>
          </ul>
          <ul className={styles.alignRight}>
            <li>
              <span className={styles.grey}>{data.v2KeolisCredits}</span>
            </li>
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            <li>
              <Link to={defaultLocale === locale ? `/${creditsPageSlug}` : `/${locale}/${creditsPageSlug}`}>
                <span>{data.credits}</span>
              </Link>
            </li>
          </ul>
      </nav>
    </footer>
  )
}

export const fragment = graphql`
  fragment FooterFields on DatoCmsFooter {
    v2Logo {
      alt
      format
      url
    }
    v2BackToTopLabel
    v2KeolisCredits
    v2SiteMapTitle
    v2SiteMapLinks {
      title
      isInternalPage
      internalLink {
        slug
      }
      externalLink
      isAnchorInPage
      anchorSection
    }
    stayConnectedTitle
    socialMedias {
      link
      image {
        alt
        format
        url
      }
    }
    groupLink {
      title
      isInternalPage
      internalLink {
        slug
      }
      externalLink
      isAnchorInPage
      anchorSection
    }
    legalMentions
    confidentialityPolicy
    cookies
    credits
  }
`

export default Footer
